import { RenderRules } from "@ronradtke/react-native-markdown-display";
import { Href, useRouter } from "expo-router";
import React, { useCallback, useMemo } from "react";
import { Alert, Linking, Platform } from "react-native";

import MarkdownRendererView from "./MarkdownRendererView";
import { Pressable, Text } from "@/components/atoms";
import { useChatTheme } from "@/features/knowledge-graph/hooks/useChatTheme";
import { usePlayerUuidLookup } from "@/features/knowledge-graph/hooks/usePlayerUuidLookup";
import { extractFirstPlayerName } from "@/features/knowledge-graph/utils/extractFirstPlayerName";

import { useNavigationContext } from "@/providers/NavigationProvider";
import { TEST_ID_GEMINI_CHAT } from "e2e/testId";
import { RADIUS } from "@/config/radius";

const isPlayerHref = (href: string | undefined): boolean =>
  !!href && (href.includes("/players/") || href.includes("player/"));

/**
 * Props for the MarkdownLink helper component used by the custom markdown link rule.
 */
interface MarkdownLinkProps {
  href?: string;
  onLinkPress: (url: string) => boolean;
  style?: Record<string, string | number>;
  children: React.ReactNode;
}

/**
 * Rendered for every markdown link. Wires the GEMINI_CHAT.PLAYER_LINK testID
 * when the href targets a player page so E2E tests can locate AI-generated
 * player links.
 * @param root0 - Component props
 * @param root0.href - The link target href from the parsed markdown node
 * @param root0.onLinkPress - Handler invoked when the link is pressed; handles
 *   all navigation internally (player links via router, external links via
 *   window.open on web or Linking.openURL on native).
 * @param root0.style - Text style applied to the link label (from the library's merged styles)
 * @param root0.children - Rendered link label content
 */
const MarkdownLink: React.FC<MarkdownLinkProps> = ({
  href,
  onLinkPress,
  style,
  children,
}) => {
  const handlePress = useCallback(() => {
    if (href) {
      onLinkPress(href);
    }
  }, [href, onLinkPress]);

  const testIDProps = isPlayerHref(href)
    ? {
        testID: TEST_ID_GEMINI_CHAT.PLAYER_LINK,
        "data-testid": TEST_ID_GEMINI_CHAT.PLAYER_LINK,
      }
    : {};

  return (
    <Pressable accessibilityRole="link" onPress={handlePress} {...testIDProps}>
      <Text
        variant="unstyled"
        // eslint-disable-next-line no-restricted-syntax -- markdown library's merged StyleProp<TextStyle> handed to the link render rule; data-driven runtime style, no closed token
        UNSAFE_style={style}
      >
        {children}
      </Text>
    </Pressable>
  );
};

const buildMarkdownRules = (
  onLinkPress: (url: string) => boolean
): RenderRules => ({
  link: (node, children, _parent, styles) => (
    <MarkdownLink
      key={node.key}
      href={node.attributes.href as string | undefined}
      onLinkPress={onLinkPress}
      // eslint-disable-next-line no-restricted-syntax -- markdown library's flattened render style forwarded to the MarkdownLink composite; data-driven runtime value, no closed token
      style={styles.link}
    >
      {children}
    </MarkdownLink>
  ),
});

/**
 * Props for the MarkdownRenderer component.
 */
interface MarkdownRendererProps {
  content: string;
  testID?: string;
}

/**
 * Container component for rendering markdown content with player link navigation support.
 * Handles player link clicks by resolving player UUIDs and navigating to player detail pages.
 * @param root0 - Component props
 * @param root0.content - Markdown content to render with syntax highlighting and custom styles
 * @param root0.testID - Optional test identifier for component testing
 */
export /**
 *
 */
const MarkdownRendererContainer: React.FC<MarkdownRendererProps> = ({
  content,
  testID,
}) => {
  const router = useRouter();
  const { pushBreadcrumb, clearBreadcrumbs } = useNavigationContext();
  const { colors } = useChatTheme();
  const playerSearchTerm = useMemo(
    () => extractFirstPlayerName(content),
    [content]
  );
  const { getPlayerUuidFromMarkdown, isLoading: isLoadingPlayers } =
    usePlayerUuidLookup(playerSearchTerm);

  const handlePlayerLink = useCallback(
    (url: string) => {
      if (isLoadingPlayers) {
        console.warn("Player data loading, please wait");
      } else {
        const sequentialId = url.includes("/players/")
          ? url.split("/players/")[1] || ""
          : url.split("player/")[1] || "";

        const playerUuid = getPlayerUuidFromMarkdown(content, sequentialId);

        if (playerUuid) {
          const playerRoute = `/players/${playerUuid}`;
          console.log("Navigating to:", playerRoute);
          clearBreadcrumbs();
          pushBreadcrumb({ label: "Ask Gemini", path: "/knowledge-graph" });
          pushBreadcrumb({
            label: playerSearchTerm || "Player",
            path: playerRoute,
          });
          router.push(playerRoute as Href);
        } else {
          console.error("Could not find player UUID for ID:", sequentialId);
          Alert.alert(
            "Navigation Error",
            "Could not find player information. Please try again.",
            [{ text: "OK" }]
          );
        }
      }

      // Return false to prevent markdown library's default Linking.openURL behavior
      // since we handle all player links with custom navigation
      return false;
    },
    [
      router,
      getPlayerUuidFromMarkdown,
      content,
      isLoadingPlayers,
      clearBreadcrumbs,
      pushBreadcrumb,
      playerSearchTerm,
    ]
  );

  const openNativeUrl = useCallback(async (url: string) => {
    await Linking.openURL(url);
  }, []);

  const handleUnsupportedUrl = useCallback((url: string) => {
    Alert.alert("Error", `Cannot open URL: ${url}`);
  }, []);

  const handleExternalLink = useCallback(
    (url: string) => {
      if (Platform.OS === "web") {
        // Use noopener,noreferrer to prevent tab-nabbing attacks
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        // Async IIFE to handle native URL opening with proper error handling
        (async () => {
          try {
            const canOpen = await Linking.canOpenURL(url);
            if (canOpen) {
              await openNativeUrl(url);
            } else {
              handleUnsupportedUrl(url);
            }
          } catch (err) {
            const message =
              err instanceof Error ? err.message : "Unknown error";
            Alert.alert("Error", `Failed to open URL: ${message}`);
          }
        })();
      }

      // Return false to prevent markdown library's default Linking.openURL behavior
      // since we handle external links ourselves (web uses window.open, native uses Linking.openURL)
      return false;
    },
    [openNativeUrl, handleUnsupportedUrl]
  );

  const handleLinkPress = useCallback(
    (url: string) => {
      if (url.includes("/players/") || url.includes("player/")) {
        return handlePlayerLink(url);
      }
      return handleExternalLink(url);
    },
    [handlePlayerLink, handleExternalLink]
  );

  /**
   * SE-5558: every style key below is a node name the markdown library
   * actually renders. Any key we omit falls back to the library's own
   * hardcoded defaults (`lib/styles.js`), which are light-theme literals —
   * `#F5F5F5` backgrounds on `blockquote`/`fence`, `#000000` on `hr`/`table`/
   * `tr` borders. In dark mode that put near-white body text on a near-white
   * block (invisible), so the map must stay exhaustive: an answer containing
   * a fenced block, blockquote or table must theme like the rest of it.
   * Text colors cascade to leaf nodes through the library's parent-style
   * flattening, so container nodes (`td`, `th`, `blockquote`, `list_item`)
   * carry the color their text should render in.
   */
  const convertedStyles = useMemo(() => {
    const { markdown } = colors;
    const fontFamily = Platform.select({
      ios: "Menlo",
      android: "monospace",
      web: "monospace",
    });
    const heading = (fontSize: number, marginVertical: number) => ({
      fontSize,
      fontWeight: "bold" as const,
      marginVertical,
      color: markdown.heading,
    });
    const codeBlock = {
      backgroundColor: markdown.codeblock.background,
      color: markdown.codeblock.text,
      borderWidth: 0,
      padding: 12,
      borderRadius: 6,
      marginVertical: 8,
      fontFamily,
    };

    return {
      body: {
        backgroundColor: markdown.background,
        color: markdown.text,
        fontSize: 14,
        lineHeight: 18,
      },

      heading1: heading(24, 8),
      heading2: heading(20, 6),
      heading3: heading(18, 4),
      heading4: heading(16, 4),
      heading5: heading(14, 4),
      heading6: heading(12, 4),

      hr: {
        backgroundColor: markdown.divider,
        height: 1,
        marginVertical: 16,
      },

      strong: { fontWeight: "bold", color: markdown.heading },
      em: { fontStyle: "italic", color: markdown.emphasis },
      s: { textDecorationLine: "line-through", color: markdown.text },

      blockquote: {
        backgroundColor: markdown.blockquote.background,
        borderColor: markdown.blockquote.border,
        borderLeftWidth: 4,
        marginVertical: 8,
        marginLeft: 5,
        paddingHorizontal: 8,
        paddingVertical: 4,
        color: markdown.text,
      },

      bullet_list: { marginVertical: 8 },
      ordered_list: { marginVertical: 8 },
      list_item: { marginVertical: 2, color: markdown.text },
      bullet_list_icon: { color: markdown.text },
      ordered_list_icon: { color: markdown.text },

      code_inline: {
        backgroundColor: markdown.codespan.background,
        color: markdown.codespan.text,
        borderWidth: 0,
        paddingHorizontal: 4,
        paddingVertical: 2,
        borderRadius: RADIUS.sm,
        fontSize: 13,
        fontFamily,
      },
      code_block: codeBlock,
      fence: codeBlock,

      table: {
        borderWidth: 1,
        borderColor: markdown.table.border,
        borderRadius: RADIUS.sm,
        marginVertical: 8,
        backgroundColor: markdown.table.background,
      },
      thead: { backgroundColor: markdown.table.headerBackground },
      tbody: {},
      tr: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderColor: markdown.table.border,
      },
      th: { flex: 1, padding: 8, color: markdown.heading },
      td: { flex: 1, padding: 8, color: markdown.text },

      link: { color: markdown.link, textDecorationLine: "underline" },
      blocklink: { borderColor: markdown.table.border },
    };
  }, [colors]);

  const containerStyle = useMemo(
    () => ({
      backgroundColor: colors.markdown.background,
      flex: 1,
      flexShrink: 1,
    }),
    [colors.markdown.background]
  );

  const rules = useMemo(
    () => buildMarkdownRules(handleLinkPress),
    [handleLinkPress]
  );

  return (
    <MarkdownRendererView
      content={content}
      testID={testID}
      convertedStyles={convertedStyles}
      containerStyle={containerStyle}
      onLinkPress={handleLinkPress}
      rules={rules}
    />
  );
};
